import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Slider } from '@/components/ui/slider';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Label } from '@/components/ui/label';
import { Search, Star, MapPin, Clock, Filter, SlidersHorizontal, Heart, X } from 'lucide-react';
import { doctorService, specialtyService, transformDoctor, transformSpecialty, getLocalizedValue, formatCurrencyAmount } from '@/services/api';
import { useFavorites } from '@/contexts/FavoritesContext';
import { useAuth } from '@/contexts/AuthContext';
import { getDefaultDoctorImage, handleImageFallback } from '@/lib/defaultImages';
import { isDoctorAccount } from '@/lib/userRoles';
import { toast } from 'sonner';

const CITIES = [
  { id: 'all', name_en: 'All Cities', name_ar: 'جميع المدن' },
  { id: 'damascus', name_en: 'Damascus', name_ar: 'دمشق' },
  { id: 'aleppo', name_en: 'Aleppo', name_ar: 'حلب' },
  { id: 'homs', name_en: 'Homs', name_ar: 'حمص' },
  { id: 'latakia', name_en: 'Latakia', name_ar: 'اللاذقية' },
  { id: 'hama', name_en: 'Hama', name_ar: 'حماة' },
];

const DEFAULT_PRICE_BOUNDS = { min: 0, max: 500 };

const normalizePrice = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatRangeAmount = (value) => {
  const amount = normalizePrice(value);
  return amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
};

const normalizeText = (value) => {
  if (typeof value !== 'string') return '';
  return value.trim().toLowerCase();
};

const specialtyNamesMatch = (left, right) => {
  const normalizedLeft = normalizeText(left);
  const normalizedRight = normalizeText(right);

  if (!normalizedLeft || !normalizedRight) return false;
  return (
    normalizedLeft === normalizedRight ||
    normalizedLeft.includes(normalizedRight) ||
    normalizedRight.includes(normalizedLeft)
  );
};

const doctorMatchesSpecialty = (doctor, selectedSpecialty, specialties) => {
  if (selectedSpecialty === 'all') return true;

  const selectedSpecialtyData = specialties.find(
    (specialty) => String(specialty.id) === String(selectedSpecialty)
  );

  if (doctor.specialty?.id != null) {
    return String(doctor.specialty.id) === String(selectedSpecialty);
  }

  const doctorSpecialtyNames = [
    doctor.specialty?.name,
    doctor.specialty?.name_ar,
    doctor.specialty?.name_en,
  ].filter(Boolean);

  const selectedSpecialtyNames = [
    selectedSpecialtyData?.name,
    selectedSpecialtyData?.name_ar,
    selectedSpecialtyData?.name_en,
  ].filter(Boolean);

  if (!doctorSpecialtyNames.length || !selectedSpecialtyNames.length) {
    return false;
  }

  return selectedSpecialtyNames.some((selectedName) =>
    doctorSpecialtyNames.some((doctorName) => specialtyNamesMatch(doctorName, selectedName))
  );
};

const getPriceBounds = (doctorList) => {
  const prices = doctorList
    .map((doctor) => normalizePrice(doctor.consultation_fee))
    .filter((price) => price > 0);

  if (!prices.length) {
    return DEFAULT_PRICE_BOUNDS;
  }

  return {
    min: 0,
    max: Math.max(DEFAULT_PRICE_BOUNDS.max, Math.ceil(Math.max(...prices) / 10) * 10),
  };
};

const extractSpecialtiesPayload = (response) => (
  response?.data?.data?.specialities ||
  response?.data?.specialities ||
  response?.data?.data ||
  response?.data ||
  []
);

const DoctorsPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [doctors, setDoctors] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState(searchParams.get('specialty') || 'all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [minRating, setMinRating] = useState(0);
  const [priceBounds, setPriceBounds] = useState(DEFAULT_PRICE_BOUNDS);
  const [priceRange, setPriceRange] = useState([DEFAULT_PRICE_BOUNDS.min, DEFAULT_PRICE_BOUNDS.max]);
  const [sortBy, setSortBy] = useState('rating');
  const [filtersOpen, setFiltersOpen] = useState(false);
  
  const { isFavorite, toggleFavorite } = useFavorites();
  const { user } = useAuth();
  const isDoctorUser = isDoctorAccount(user);
  const lang = i18n.language || 'ar';
  const selectedClinicId = searchParams.get('clinic_id') || '';
  const selectedSpecialtyLabel =
    selectedSpecialty === 'all'
      ? ''
      : specialties.find((specialty) => String(specialty.id) === String(selectedSpecialty))?.name || '';

  // Initialize search from URL params
  useEffect(() => {
    const urlSearch = searchParams.get('search');
    if (urlSearch) {
      setSearchQuery(urlSearch);
    }
  }, [searchParams]);

  useEffect(() => {
    setSelectedSpecialty(searchParams.get('specialty') || 'all');
  }, [searchParams]);

  // Fetch doctors - fetch clinic-scoped results when opened from a clinic page.
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        console.log('Fetching doctors...');

        const doctorParams = selectedClinicId ? { clinic_id: selectedClinicId } : undefined;
        const [doctorsRes, specialtiesRes] = await Promise.all([
          doctorService.getAllDoctors(doctorParams),
          specialtyService.getAll(),
        ]);
        
        console.log('API Response:', doctorsRes.data);
        
        // Handle different API response structures
        // /all-doctors returns: { success, data: [...doctors], message }
        let rawDoctors = [];
        const resData = doctorsRes.data.data || doctorsRes.data;
        if (Array.isArray(resData)) {
          rawDoctors = resData;
        } else if (resData && Array.isArray(resData.doctors)) {
          rawDoctors = resData.doctors;
        }
        
        const rawSpecialties = extractSpecialtiesPayload(specialtiesRes);
        const normalizedSpecialties = Array.isArray(rawSpecialties) ? rawSpecialties : [];
        
        if (rawDoctors.length > 0) {
          // Transform doctors without modifying their specialty
          const transformedDoctors = rawDoctors.map(d => transformDoctor(d, lang));
          setDoctors(transformedDoctors);
          const bounds = getPriceBounds(transformedDoctors);
          setPriceBounds(bounds);
          setPriceRange([bounds.min, bounds.max]);
        } else {
          setDoctors([]);
          setPriceBounds(DEFAULT_PRICE_BOUNDS);
          setPriceRange([DEFAULT_PRICE_BOUNDS.min, DEFAULT_PRICE_BOUNDS.max]);
        }
        
        setSpecialties(normalizedSpecialties.map(s => transformSpecialty(s, lang)));
      } catch (error) {
        console.error('Failed to fetch doctors data:', error);
        setDoctors([]);
        setSpecialties([]);
        setPriceBounds(DEFAULT_PRICE_BOUNDS);
        setPriceRange([DEFAULT_PRICE_BOUNDS.min, DEFAULT_PRICE_BOUNDS.max]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [lang, selectedClinicId]); // Only re-fetch when language or clinic scope changes.

  const filteredDoctors = doctors
    .filter(doctor => {
      // Search across multiple fields in both languages
      const searchLower = normalizeText(searchQuery);
      const matchesSearch = !searchQuery || 
        normalizeText(doctor.name).includes(searchLower) ||
        normalizeText(doctor.name_en).includes(searchLower) ||
        normalizeText(doctor.name_ar).includes(searchLower) ||
        normalizeText(doctor.specialty?.name).includes(searchLower) ||
        normalizeText(doctor.specialty?.name_ar).includes(searchLower) ||
        normalizeText(doctor.specialty?.name_en).includes(searchLower) ||
        normalizeText(doctor.bio).includes(searchLower) ||
        normalizeText(doctor.bio_en).includes(searchLower) ||
        normalizeText(doctor.bio_ar).includes(searchLower) ||
        normalizeText(doctor.clinic?.name).includes(searchLower);

      const matchesSpecialty = doctorMatchesSpecialty(
        doctor,
        selectedSpecialty,
        specialties
      );
      
      const matchesCity = selectedCity === 'all' || 
        normalizeText(doctor.city) === normalizeText(selectedCity);
      const matchesRating = doctor.rating >= minRating;
      const consultationFee = normalizePrice(doctor.consultation_fee);
      const matchesPrice = consultationFee >= priceRange[0] && consultationFee <= priceRange[1];
      const matchesClinic =
        !selectedClinicId ||
        String(doctor.clinic_id || doctor.clinic?.id || '') === String(selectedClinicId);

      return matchesSearch && matchesSpecialty && matchesCity && matchesRating && matchesPrice && matchesClinic;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'rating': return (b.rating || 0) - (a.rating || 0);
        case 'price_low': return (a.consultation_fee || 0) - (b.consultation_fee || 0);
        case 'price_high': return (b.consultation_fee || 0) - (a.consultation_fee || 0);
        case 'experience': return (b.experience_years || 0) - (a.experience_years || 0);
        default: return 0;
      }
    });

  const handleSpecialtyChange = (value) => {
    setSelectedSpecialty(value);
    const nextParams = new URLSearchParams(searchParams);

    if (value === 'all') {
      nextParams.delete('specialty');
    } else {
      nextParams.set('specialty', value);
    }

    setSearchParams(nextParams);
  };

  const handleFavoriteClick = (doctor, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDoctorUser) return;

    toggleFavorite(doctor);
    toast.success(
      isFavorite(doctor.id) 
        ? (lang === 'ar' ? 'تمت الإزالة من المفضلة' : 'Removed from favorites')
        : (lang === 'ar' ? 'تمت الإضافة إلى المفضلة' : 'Added to favorites')
    );
  };

  const clearFilters = () => {
    setSelectedSpecialty('all');
    setSelectedCity('all');
    setMinRating(0);
    setPriceRange([priceBounds.min, priceBounds.max]);
    setSortBy('rating');
    setSearchQuery('');
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('specialty');
    setSearchParams(nextParams);
  };

  const activeFiltersCount = [
    selectedSpecialty !== 'all',
    selectedCity !== 'all',
    minRating > 0,
    priceRange[0] !== priceBounds.min || priceRange[1] !== priceBounds.max,
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="doctors-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="doctors-title">
            {t('doctors.title')}
          </h1>
          <p className="text-slate-600 dark:text-slate-400">{lang === 'ar' ? 'ابحث واحجز موعدًا مع أفضل الأطباء' : 'Find and book appointments with top healthcare professionals'}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 sticky top-16 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t('home.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <Select value={selectedSpecialty} onValueChange={handleSpecialtyChange}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="specialty-filter">
                <SelectValue placeholder={t('doctors.filterBySpecialty')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('doctors.allSpecialties')}</SelectItem>
                {specialties.map((specialty) => (
                  <SelectItem key={specialty.id} value={String(specialty.id)}>
                    {specialty.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-full sm:w-[150px]" data-testid="sort-filter">
                <SelectValue placeholder={lang === 'ar' ? 'ترتيب حسب' : 'Sort by'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="rating">{lang === 'ar' ? 'التقييم' : 'Rating'}</SelectItem>
                <SelectItem value="price_low">{lang === 'ar' ? 'السعر: الأقل' : 'Price: Low to High'}</SelectItem>
                <SelectItem value="price_high">{lang === 'ar' ? 'السعر: الأعلى' : 'Price: High to Low'}</SelectItem>
              </SelectContent>
            </Select>

            {/* Advanced Filters Sheet */}
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="relative" data-testid="advanced-filters-btn">
                  <SlidersHorizontal className="h-4 w-4 mr-2" />
                  {lang === 'ar' ? 'فلاتر' : 'Filters'}
                  {activeFiltersCount > 0 && (
                    <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center bg-teal-600">
                      {activeFiltersCount}
                    </Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side={lang === 'ar' ? 'left' : 'right'} className="w-[320px]">
                <SheetHeader>
                  <SheetTitle className="flex items-center justify-between">
                    {lang === 'ar' ? 'الفلاتر' : 'Filters'}
                    {activeFiltersCount > 0 && (
                      <Button variant="ghost" size="sm" onClick={clearFilters}>
                        <X className="h-4 w-4 mr-1" />
                        {lang === 'ar' ? 'مسح الكل' : 'Clear all'}
                      </Button>
                    )}
                  </SheetTitle>
                </SheetHeader>
                
                <div className="mt-6 space-y-6">
                  {/* Specialty Filter */}
                  <div className="space-y-2">
                    <Label>{lang === 'ar' ? 'التخصص' : 'Specialty'}</Label>
                    <Select value={selectedSpecialty} onValueChange={handleSpecialtyChange}>
                      <SelectTrigger
                        data-testid="sheet-specialty-filter"
                        className="h-11 rounded-xl border-slate-700 bg-slate-900/70 text-right text-white transition-colors hover:border-teal-500 focus:border-teal-500"
                      >
                        <SelectValue placeholder={t('doctors.filterBySpecialty')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('doctors.allSpecialties')}</SelectItem>
                        {specialties.map((specialty) => (
                          <SelectItem key={specialty.id} value={String(specialty.id)}>
                            {specialty.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedSpecialtyLabel && (
                      <div className="rounded-xl border border-teal-500/30 bg-teal-500/10 px-3 py-2 text-sm font-medium text-teal-300">
                        {lang === 'ar' ? 'التخصص المحدد: ' : 'Selected specialty: '}
                        <span className="text-white">{selectedSpecialtyLabel}</span>
                      </div>
                    )}
                  </div>
                  {/* City Filter */}
                  <div className="space-y-2">
                    <Label>{lang === 'ar' ? 'المدينة' : 'City'}</Label>
                    <Select value={selectedCity} onValueChange={setSelectedCity}>
                      <SelectTrigger
                        data-testid="city-filter"
                        className="h-11 rounded-xl border-slate-700 bg-slate-900/70 text-right text-white transition-colors hover:border-teal-500 focus:border-teal-500"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CITIES.map((city) => (
                          <SelectItem key={city.id} value={city.id}>
                            {lang === 'ar' ? city.name_ar : city.name_en}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Rating Filter */}
                  <div className="space-y-3">
                    <Label>{lang === 'ar' ? 'الحد الأدنى للتقييم' : 'Minimum Rating'}</Label>
                    <div className="flex flex-wrap gap-2">
                      {[0, 3, 3.5, 4, 4.5].map((rating) => (
                        <Button
                          key={rating}
                          variant={minRating === rating ? 'default' : 'outline'}
                          size="sm"
                          className={`h-10 min-w-[58px] rounded-xl border-slate-700 px-3 ${
                            minRating === rating
                              ? 'bg-teal-600 text-white hover:bg-teal-700'
                              : 'bg-slate-900/60 text-slate-200 hover:border-teal-500 hover:bg-slate-900'
                          }`}
                          onClick={() => setMinRating(rating)}
                        >
                          {rating === 0 ? (lang === 'ar' ? 'الكل' : 'All') : (
                            <><Star className="h-3 w-3 fill-current mr-1" />{rating}+</>
                          )}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Price Range Filter */}
                  <div className="space-y-3">
                    <Label>{lang === 'ar' ? 'نطاق السعر' : 'Price Range'}</Label>
                    <Slider
                      value={priceRange}
                      onValueChange={setPriceRange}
                      min={priceBounds.min}
                      max={priceBounds.max}
                      step={10}
                      className="mt-2"
                    />
                    <div className="flex justify-between text-sm text-slate-500 dark:text-slate-400">
                      <span>{formatRangeAmount(priceRange[0])}</span>
                      <span>{formatRangeAmount(priceRange[1])}</span>
                    </div>
                  </div>

                  <Button 
                    className="w-full bg-teal-600 hover:bg-teal-700 mt-4" 
                    onClick={() => setFiltersOpen(false)}
                  >
                    {lang === 'ar' ? 'تطبيق الفلاتر' : 'Apply Filters'}
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Results Count */}
      <div className="container mx-auto px-4 py-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {lang === 'ar' 
            ? `عرض ${filteredDoctors.length} من ${doctors.length} طبيب`
            : `Showing ${filteredDoctors.length} of ${doctors.length} doctors`
          }
        </p>
      </div>

      {/* Doctor List */}
      <div className="container mx-auto px-4 pb-8">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <Skeleton className="h-24 w-24 rounded-xl" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                      <Skeleton className="h-4 w-2/3" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredDoctors.length === 0 ? (
          <div className="text-center py-12" data-testid="no-doctors-message">
            <p className="text-slate-600 mb-4">{t('common.noData')}</p>
            <Button variant="outline" onClick={clearFilters}>
              {lang === 'ar' ? 'مسح الفلاتر' : 'Clear Filters'}
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredDoctors.map((doctor) => (
              <Card 
                key={doctor.id} 
                className="dark:bg-slate-800 border-0 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 relative"
                data-testid={`doctor-card-${doctor.id}`}
              >
                {/* Favorite Button */}
                {!isDoctorUser && (
                  <button
                    onClick={(e) => handleFavoriteClick(doctor, e)}
                    className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 hover:bg-white shadow-sm transition-colors"
                    data-testid={`favorite-btn-${doctor.id}`}
                  >
                    <Heart
                      className={`h-5 w-5 transition-colors ${
                        isFavorite(doctor.id)
                          ? 'fill-red-500 text-red-500'
                          : 'text-slate-400 hover:text-red-400'
                      }`}
                    />
                  </button>
                )}

                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <img
                      src={doctor.image || getDefaultDoctorImage(doctor.gender)}
                      alt={doctor.name}
                      className="h-24 w-24 rounded-xl object-cover"
                      onError={handleImageFallback(getDefaultDoctorImage(doctor.gender))}
                    />
                    <div className="flex-1 min-w-0">
                      <div className={`flex items-start justify-between gap-2 ${isDoctorUser ? '' : 'pr-8'}`}>
                        <h3 className="font-semibold text-slate-900 truncate">{doctor.name}</h3>
                      </div>
                      <p className="text-sm text-teal-600 mb-2">{doctor.specialty?.name}</p>
                      <div className="flex items-center gap-2 text-sm text-slate-600 mb-1">
                        <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                        <span className="font-medium">{doctor.rating}</span>
                        <span className="text-slate-400">({doctor.reviews_count} {t('doctors.reviews')})</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-slate-500 dark:text-slate-400">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{doctor.experience_years} {t('doctors.years')}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex min-h-[1.25rem] items-center gap-3 mb-3">
                      <div className="min-w-0 flex-1">
                        {doctor.address && (
                          <div className="flex min-w-0 items-center gap-1 text-sm text-slate-500 dark:text-slate-400">
                            <MapPin className="h-3.5 w-3.5" />
                            <span className="truncate">{doctor.address}</span>
                          </div>
                        )}
                      </div>
                      {doctor.consultation_fee > 0 && (
                        <span className="shrink-0 text-sm font-semibold text-teal-600">
                          {formatCurrencyAmount(doctor.consultation_fee, doctor)}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" asChild>
                        <Link to={`/doctors/${doctor.id}`}>{t('doctors.viewProfile')}</Link>
                      </Button>
                      {!isDoctorUser && (
                        <Button size="sm" className="flex-1 bg-teal-600 hover:bg-teal-700" asChild>
                          <Link to={`/doctors/${doctor.id}`}>{t('doctors.bookNow')}</Link>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorsPage;
